/**
 * F 340 — the Agent Tool Layer's tool schemas.
 * F 356 — extended with update_node, create_database, create_view.
 *
 * The list is deliberately closed. `TOOL_NAMES` is what the Operator is
 * told it may call, `executeTool` is what it is actually allowed to run,
 * and both read from the SAME `TOOL_NAMES` constant. A new name cannot be
 * introduced by adding it to the prompt without also opening the executor,
 * because there is no second list to forget to update.
 *
 * PERMISSIONS ARE NOT HERE. A schema describes what a tool looks like; it has
 * no authority. The allowlist that decides what may actually happen lives in
 * `execute.ts`, so a caller that builds its own arguments — including the
 * Operator model — cannot widen it by describing a different tool.
 *
 * DELETION HAS NO SCHEMA ON PURPOSE. There is no `delete_node` to describe,
 * which is the first line of defence; `execute.ts` refuses destructive verbs
 * again at the boundary, which is the second. Defence in depth, because the
 * model is untrusted input and a prompt is not a permission.
 */

/** Every tool this build exposes. The single source of truth for both halves. */
export const TOOL_NAMES = [
  'create_node',
  'query_graph',
  'update_node',
  'create_database',
  'create_view',
] as const;

export type ToolName = (typeof TOOL_NAMES)[number];

/**
 * Kinds this dispatch permits for `create_node` and `query_graph`. The two
 * pre-existing EntityKind members that F 340 chose. Adding 'database' here
 * would conflate "create a page" with "create a database" — those are
 * different tools with different shapes, and create_database has its own
 * implicit kind ('database', set by the executor). Keeping the list short
 * also keeps the schema prompt small and unambiguous.
 *
 * F 382 — 'view' added, AUTHORISED by the F 382 dispatch.
 *
 * WHY IT IS SAFE, since F 340's reasoning above was "keep the list short":
 * `create_node` is the only WRITER in this list, and `view` is NOT admitted
 * there — `create_node` validates through `isAllowedKind` too, so widening
 * this const would also let the agent fabricate a view node by hand, which
 * is NOT wanted. That is why the executor refuses `create_node` for 'view'
 * explicitly (see `VIEW_READ_ONLY_KINDS` in execute.ts). `query_graph` is
 * read-only, so admitting 'view' there adds a read surface and no write
 * surface.
 *
 * WHAT A VIEW READ COSTS: `query_graph` with kind='view' returns ONLY
 * `{ databaseId, name, layout }`. It deliberately does NOT return the
 * `filter` field, because the UI's `PropertyFilter` (grouped OR) and core's
 * `FilterRule[]` (flat AND) are not the same shape — F 379 measured that
 * flattening one into the other silently returns zero rows, which is why the
 * F 280 Item-4 unification was deferred. Exposing only identity + layout
 * keeps the read honest and leaves the unification as its own dispatch.
 *
 * FVC 1222 — 'decision' added, AUTHORISED by the FVC 1222 dispatch.
 *
 * WHY A DECISION IS SAFE HERE, and why it needed a different argument than
 * 'view' did. F 382 added 'view' as a READ surface and then had to REFUSE it
 * on the write path, because a view is owned by the UI registry. A decision
 * is the opposite case: the decision IS the write. A Decision Record is a
 * first-class entity in the FC Brain (`src/types/models/Decision.ts`) with a
 * real lifecycle (`proposed` -> `accepted` / `rejected` / `superseded`) and a
 * real `supersede()` that marks rather than deletes. Until this dispatch the
 * agent could not author one at all, which is why the Decision Graph's read
 * path was live and its write path was dead.
 *
 * 'decision' is DATA-MODEL-ONLY (see `src/core/adapters/filesystem/types.ts`):
 * the filesystem adapter will never emit one, because a file on disk cannot
 * be a decision. That is the same classification 'prompt', 'build', 'turn',
 * 'conversation', 'group', 'agent' and 'span' already carry, and it is NOT a
 * reason to refuse the kind. The agent writes through `context.storage`, which
 * is the PocketBase adapter in production — it is not the filesystem adapter.
 *
 * WHAT A DECISION WRITE MUST STILL RESPECT:
 *   - it does NOT widen the read surface for 'view'. The VIEW_READ_ONLY_KINDS
 *     refusal below is a separate, later check and is untouched by this
 *     change. 'view' still cannot be minted by hand.
 *   - 'database' is STILL refused. create_database owns that kind, and
 *     conflating the two would be the exact mistake F 340 warned about.
 *   - the list stays CLOSED. This dispatch adds exactly one member.
 */
export const ALLOWED_KINDS = ['page', 'task', 'view', 'decision'] as const;

/**
 * Kinds that `query_graph` may READ but `create_node` may NOT WRITE.
 *
 * F 382. A view is owned by the UI registry (`viewConfig.ts`) and the AI's
 * only legitimate view write is `create_view`, which validates the parent
 * database exists before appending. Handing `create_node` a 'view' would let
 * the agent mint an orphan view with no database behind it.
 */
export const VIEW_READ_ONLY_KINDS = ['view'] as const;

export type AllowedKind = (typeof ALLOWED_KINDS)[number];

/**
 * Kinds accepted across the broader layer, used to LOOK UP an existing node
 * in update_node. The executor searches each kind's namespace (`page:`, `task:`,
 * `database:`) for the id, so an update_node caller doesn't have to know which
 * kind the node is — the substrate resolves it. This list is wider than
 * ALLOWED_KINDS on purpose: it's a *read* surface for the update path, not
 * a write surface.
 */
export const SEARCHABLE_KINDS = ['page', 'task', 'database'] as const;

export interface CreateNodeArgs {
  kind: AllowedKind;
  title: string;
  parentId?: string | null;
}

export interface QueryGraphArgs {
  kind: AllowedKind;
  limit: number;
  sortBy: 'createdAt' | 'title';
}

export interface UpdateNodeArgs {
  /** id of the existing node, as returned by create_node. */
  id: string;
  title?: string;
  /** Optional page body, markdown. Applied when the target is a page-kind. */
  content?: string;
}

export interface CreateDatabaseArgs {
  title: string;
  columns: Array<{ name: string; type: DatabaseColumnType }>;
}

export interface CreateViewArgs {
  databaseId: string;
  name: string;
  /** 'gallery' | 'kanban' | 'list'. The dispatch calls kanban 'board'. */
  type: 'gallery' | 'kanban' | 'list';
}

/**
 * F 356 — column types the executor accepts.
 *
 * Why a closed list rather than the full `PropertyType` union: the executor
 * is the permission layer, and a closed list is the simplest expression of
 * "the AI may create columns of these kinds". The full `PropertyType` union
 * lives in `src/types/models/Property.ts` and includes things like
 * 'formula' that the AI should not be able to author (the formula would
 * evaluate at read time and could leak data). Future expansions add to
 * THIS list, not to the underlying type.
 */
export const ALLOWED_COLUMN_TYPES = [
  'title',
  'text',
  'number',
  'select',
  'multi_select',
  'date',
  'checkbox',
  'url',
  'status',
] as const;

export type DatabaseColumnType = (typeof ALLOWED_COLUMN_TYPES)[number];

export interface ToolSchema {
  name: ToolName;
  description: string;
  /** JSON Schema, as handed to the Operator in the system prompt. */
  parameters: Record<string, unknown>;
}

export const CREATE_NODE: ToolSchema = {
  name: 'create_node',
  description:
    'Create one node in the FreshCards graph. Use this when the user asks to ' +
    'make a page, task, note or similar. To create a database, use create_database.',
  parameters: {
    type: 'object',
    required: ['kind', 'title'],
    additionalProperties: false,
    properties: {
      kind: {
        type: 'string',
        enum: [...ALLOWED_KINDS],
        description: 'What sort of node to create. Permitted: page, task, database.',
      },
      title: {
        type: 'string',
        minLength: 1,
        maxLength: 200,
        description: "The node's title, taken from what the user asked for.",
      },
      parentId: {
        type: ['string', 'null'],
        description: 'Optional id of a parent node. Omit or null for a root node.',
      },
    },
  },
};

export const QUERY_GRAPH: ToolSchema = {
  name: 'query_graph',
  description:
    'Read nodes of one kind out of the FreshCards graph. Use this when the ' +
    'user asks what exists, or to look something up.',
  parameters: {
    type: 'object',
    required: ['kind', 'limit', 'sortBy'],
    additionalProperties: false,
    properties: {
      kind: { type: 'string', enum: [...ALLOWED_KINDS] },
      limit: {
        type: 'integer',
        minimum: 1,
        maximum: 100,
        description: 'How many nodes to return at most.',
      },
      sortBy: {
        type: 'string',
        enum: ['createdAt', 'title'],
        description: 'Field to order the result by.',
      },
      // F 382 — only honoured for kind='view', where it narrows to one
      // database. Without it the schema's `additionalProperties: false` would
      // make the natural question ("what views does Quick Notes have?")
      // unaskable. For page/task it is ignored.
      filter: {
        type: 'object',
        additionalProperties: false,
        properties: {
          databaseId: {
            type: 'string',
            description:
              "Narrow a view read to one database's id. Only used when kind='view'.",
          },
        },
      },
    },
  },
};

export const UPDATE_NODE: ToolSchema = {
  name: 'update_node',
  description:
    'Update one existing node. Use this when the user asks to rename, edit ' +
    'the body of, or otherwise change a page or task they have already named. ' +
    'Pass only the fields you want to change; the others are left as they are.',
  parameters: {
    type: 'object',
    required: ['id'],
    additionalProperties: false,
    properties: {
      id: {
        type: 'string',
        minLength: 1,
        description: 'The id of the node to update. Page ids look like "page-..."; task ids look like "task_...".',
      },
      title: {
        type: 'string',
        minLength: 1,
        maxLength: 200,
        description: 'New title for the node. Omit to leave it alone.',
      },
      content: {
        type: 'string',
        description: 'New body content (markdown) for a page-kind node. Omit to leave the body alone.',
      },
    },
  },
};

export const CREATE_DATABASE: ToolSchema = {
  name: 'create_database',
  description:
    'Create a database. A database in FreshCards is a page with a schema: ' +
    'one or more named columns of typed values. Always returns the new ' +
    "database's id, which you can use with create_view to add a view.",
  parameters: {
    type: 'object',
    required: ['title', 'columns'],
    additionalProperties: false,
    properties: {
      title: {
        type: 'string',
        minLength: 1,
        maxLength: 200,
        description: "The database's display title.",
      },
      columns: {
        type: 'array',
        minItems: 1,
        maxItems: 32,
        items: {
          type: 'object',
          required: ['name', 'type'],
          additionalProperties: false,
          properties: {
            name: { type: 'string', minLength: 1, maxLength: 64 },
            type: { type: 'string', enum: [...ALLOWED_COLUMN_TYPES] },
          },
        },
        description: 'The schema. The first entry becomes the kind column (status field).',
      },
    },
  },
};

export const CREATE_VIEW: ToolSchema = {
  name: 'create_view',
  description: [
    "Create a view on an existing database. A view is how a database is rendered: ",
    "'gallery' is an image grid, 'kanban' is a board grouped by the kind column, ",
    "'list' is a simple list."
  ].join(''),
  parameters: {
    type: 'object',
    required: ['databaseId', 'name', 'type'],
    additionalProperties: false,
    properties: {
      databaseId: {
        type: 'string',
        minLength: 1,
        description: "The id of the database to attach the view to.",
      },
      name: {
        type: 'string',
        minLength: 1,
        maxLength: 64,
        description: 'Display name for the view.',
      },
      type: {
        type: 'string',
        enum: ['gallery', 'kanban', 'list'],
        description: "Layout for the view. The dispatch's 'board' maps to 'kanban'.",
      },
    },
  },
};

export const TOOL_SCHEMAS: Record<ToolName, ToolSchema> = {
  create_node: CREATE_NODE,
  query_graph: QUERY_GRAPH,
  update_node: UPDATE_NODE,
  create_database: CREATE_DATABASE,
  create_view: CREATE_VIEW,
};

/** Every schema as one array, for the prompt. */
export function toolSchemas(): ToolSchema[] {
  return TOOL_NAMES.map((n) => TOOL_SCHEMAS[n]);
}

/**
 * The system prompt that turns the Operator into a tool caller.
 *
 * Measured before this file was written (F 340 STOP-IF 1): with an equivalent
 * instruction, all three live models — groq/openai/gpt-oss-120b,
 * nvidia/z-ai/glm-5.3 and gemini/gemini-3-flash-preview — returned a
 * parseable JSON tool call for "Create a page called Test". Gemini only
 * obeyed once its instruction was sent as `systemInstruction`; passed as an
 * ordinary message it answered in prose and never emitted JSON. That is why
 * the caller must pass this as a SYSTEM message on every transport.
 */
export function operatorSystemPrompt(): string {
  const schema = JSON.stringify(toolSchemas(), null, 2);
  return [
    'You are the FreshCards Operator. You act on the app by calling tools.',
    '',
    'Reply with ONLY a single JSON object. No prose, no explanation, no markdown fence.',
    'To act, reply: {"tool":"<tool name>","args":{...}}',
    'To answer without acting, reply: {"tool":"reply","args":{"message":"<your answer>"}}',
    '',
    `Permitted kinds are exactly: ${ALLOWED_KINDS.join(', ')}.`,
    'Deletion is not permitted. If asked to delete or remove anything, reply with:',
    '{"tool":"reply","args":{"message":"Deletion is not permitted."}}',
    '',
    'Available tools:',
    schema,
  ].join('\n');
}

export interface ParsedToolCall {
  tool: string;
  args: Record<string, unknown>;
}

/**
 * Parse an Operator reply into a tool call.
 *
 * Tolerates a markdown fence and surrounding prose, because measured replies
 * from real models include both. Returns null when nothing usable is present,
 * so the caller can ask again rather than execute a guess.
 */
export function parseToolCall(text: string): ParsedToolCall | null {
  if (typeof text !== 'string') return null;
  const fence = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidate = fence ? fence[1] : text;
  const braced = candidate.match(/\{[\s\S]*\}/);
  if (!braced) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(braced[0]);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const o = parsed as Record<string, unknown>;
  if (typeof o.tool !== 'string') return null;
  const args = (o.args ?? {}) as Record<string, unknown>;
  if (!args || typeof args !== 'object' || Array.isArray(args)) return null;
  return { tool: o.tool, args };
}

/**
 * F 467 — the reply envelope is a CONTAINER, not the message.
 *
 * MEASURED 2026-10-23 (production, build d655007). The operator typed "hi"
 * and the chat window printed
 *
 *   {"tool":"reply","args":{"message":"Hello!..."}}
 *
 * verbatim. The Operator is TOLD to answer in exactly that shape — see
 * `operatorSystemPrompt` above — so the envelope is the transport, and the
 * message the user should read is the string inside `args.message`.
 *
 * THE CAUSE WAS A PRECEDENCE BUG, NOT A PARSE FAILURE. `parseToolCall`
 * parsed that envelope correctly: `call.tool === 'reply'` and
 * `call.args.message` held the greeting. But the render did
 *
 *   turn1.text || call.args.message
 *
 * and `turn1.text` is the raw JSON, which is truthy — so the container won
 * and the message underneath it was never read. The SECOND turn already
 * unwrapped the envelope correctly; only the FIRST turn did not, which is
 * exactly why the bug showed on the first reply and not on summaries.
 *
 * THE RULES:
 *   1. a `reply` envelope -> render `args.message`, and nothing else
 *   2. plain prose        -> render it as is
 *   3. any other tool     -> not a reply; the caller decides
 *
 * Rule 3 is why this returns the text unchanged for a non-reply call: the
 * tool path renders the tool line itself and must not lose the raw reply.
 */
export function unwrapReplyEnvelope(
  text: string,
  call?: ParsedToolCall | null,
): string {
  // `undefined` means "you parse it"; `null` means "there is no envelope",
  // which is different from "not looked yet" and must not be re-parsed.
  const c = call === undefined ? parseToolCall(text) : call;
  if (c?.tool !== 'reply') return text;
  const message = (c.args as Record<string, unknown>)?.message;
  if (typeof message === 'string') return message;
  // A reply envelope with no message field at all. There is nothing to
  // unwrap, so show what actually arrived rather than inventing a message.
  return text;
}